import { i18n } from "~vue/i18n";

import type { WeeklyActionChannel } from "./weeklyActions";

export type WeeklyActionChannelPresentation = {
    label: string;
    description: string;
    icon: string;
    iconClass: string;
};

export type WeeklyActionModePresentation = {
    label: string;
    icon: string;
    theme: "work" | "nadia";
};

export type WeeklyActionTaxonomyPresentation = {
    label: string;
    icon: string;
    kind: "resource" | "tag";
    theme: "work" | "nadia";
};

// Each getter builds its record fresh per call (rather than once at module scope) so the
// translated labels stay reactive to the active locale when called from inside a computed().
export const getWeeklyActionChannelPresentation = (channel: WeeklyActionChannel): WeeklyActionChannelPresentation => {
    const { t } = i18n.global;
    const channelPresentations: Record<WeeklyActionChannel, WeeklyActionChannelPresentation> = {
        learning_material: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.learningMaterial.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.learningMaterial.description"),
            icon: "bi-book",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
        role_play_with_nadia: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.rolePlayWithNadia.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.rolePlayWithNadia.description"),
            icon: "bi-person-video2",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
        conversation_with_nadia: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.conversationWithNadia.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.conversationWithNadia.description"),
            icon: "bi-chat-square-text",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
        flow_of_work_meeting: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkMeeting.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkMeeting.description"),
            icon: "bi-people",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
        flow_of_work_activity: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkActivity.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkActivity.description"),
            icon: "bi-briefcase",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
        action_review_reminder: {
            label: t("developmentJourneys.weeklyActionPresentation.channel.actionReviewReminder.label"),
            description: t("developmentJourneys.weeklyActionPresentation.channel.actionReviewReminder.description"),
            icon: "bi-bell",
            iconClass: "bg-chip-neutral-bg text-chip-neutral-text",
        },
    };
    return channelPresentations[channel];
};

export const getWeeklyActionModePresentation = (channel: WeeklyActionChannel): WeeklyActionModePresentation => {
    const { t } = i18n.global;
    const onYourOwn = t("developmentJourneys.weeklyActionPresentation.mode.onYourOwn");
    const actionModePresentations: Record<WeeklyActionChannel, WeeklyActionModePresentation> = {
        learning_material: { label: onYourOwn, icon: "bi-pencil", theme: "work" },
        role_play_with_nadia: { label: t("developmentJourneys.weeklyActionPresentation.mode.rolePlayWithNadia"), icon: "bi-chat", theme: "nadia" },
        conversation_with_nadia: { label: t("developmentJourneys.weeklyActionPresentation.mode.withNadia"), icon: "bi-chat", theme: "nadia" },
        flow_of_work_meeting: { label: onYourOwn, icon: "bi-pencil", theme: "work" },
        flow_of_work_activity: { label: onYourOwn, icon: "bi-pencil", theme: "work" },
        action_review_reminder: { label: onYourOwn, icon: "bi-pencil", theme: "work" },
    };
    return actionModePresentations[channel];
};

export const getWeeklyActionTaxonomyPresentation = (channel: WeeklyActionChannel): WeeklyActionTaxonomyPresentation => {
    const { t } = i18n.global;
    const actionTaxonomyPresentations: Record<WeeklyActionChannel, WeeklyActionTaxonomyPresentation> = {
        learning_material: { label: t("developmentJourneys.weeklyActionPresentation.channel.learningMaterial.label"), icon: "bi-file-richtext", kind: "resource", theme: "work" },
        role_play_with_nadia: { label: t("developmentJourneys.weeklyActionPresentation.mode.rolePlayWithNadia"), icon: "bi-people", kind: "tag", theme: "nadia" },
        conversation_with_nadia: { label: t("developmentJourneys.weeklyActionPresentation.mode.withNadia"), icon: "bi-chat-left", kind: "tag", theme: "nadia" },
        flow_of_work_meeting: { label: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkMeeting.label"), icon: "bi-calendar", kind: "tag", theme: "work" },
        flow_of_work_activity: { label: t("developmentJourneys.weeklyActionPresentation.channel.flowOfWorkActivity.label"), icon: "bi-pencil", kind: "tag", theme: "work" },
        action_review_reminder: { label: t("developmentJourneys.weeklyActionPresentation.channel.actionReviewReminder.label"), icon: "bi-bell", kind: "tag", theme: "work" },
    };
    return actionTaxonomyPresentations[channel];
};
